const baseUrl = process.env.BASE_URL ?? 'http://localhost:3000'

const baseCdnUrl = process.env.NEXT_PUBLIC_CDN_URL
const nextBuildId = process.env.NEXT_PUBLIC_BUILD_ID

const environment = {
  development: 'development',
  preview: 'preview',
  build: 'build',
  stage: 'stage',
  production: 'production'
} as const

type EnvironmentName = typeof environment[keyof typeof environment]

const getCurrentEnvironment = (defaultEnvironment: EnvironmentName): EnvironmentName => {
  const env = process.env.APP_ENV ?? process.env.NODE_ENV

  if (typeof env !== 'undefined') {
    /* TODO: We can't know for certain that the value from process.env is
    of the correct type though it should be! Worth doing some additional checking here? */
    return env as EnvironmentName
  }

  return defaultEnvironment
}

const currentEnv = getCurrentEnvironment(environment.development)

const isProductionEnv = (): boolean => currentEnv === environment.production
const isDevelopmentEnv = (): boolean => currentEnv === environment.development

const urlEncode = (value: string | number | boolean): string => {
  try {
    return encodeURIComponent(value)
  } catch {
    return value.toString()
  }
}

const joinUrlSegments = (segments: string[]): string => {
  if (segments.length === 0) {
    return ''
  }

  const lastSegmentIndex = segments.length - 1

  const urlSegments = segments.map((segment, index) => {
    let urlSegment =
      index > 0 && segment.startsWith('/') ? segment.slice(1) : segment
    urlSegment =
      index < lastSegmentIndex && urlSegment.endsWith('/')
        ? urlSegment.slice(0, -1)
        : urlSegment

    return urlSegment
  })

  return urlSegments.join('/')
}

const getAbsoluteUrl = (path?: string): string => {
  if (typeof path === 'undefined') {
    return baseUrl
  }

  return joinUrlSegments([baseUrl, path])
}

const getCdnUrl = (path?: string, appendBuildId = true): string => {
  if (typeof baseCdnUrl === 'undefined') {
    return path ?? ''
  }

  if (typeof path === 'undefined') {
    return ''
  }

  if (appendBuildId && typeof nextBuildId !== 'undefined') {
    return joinUrlSegments([baseCdnUrl, nextBuildId, path])
  }

  return joinUrlSegments([baseCdnUrl, path])
}

const isSameOrigin = (url: string | URL): boolean => {
  try {
    const srcUrl = new URL(url)
    const appUrl = new URL(baseUrl)

    return srcUrl.origin === appUrl.origin
  } catch {
    return false
  }
}

export {
  isProductionEnv,
  isDevelopmentEnv,
  currentEnv,
  getAbsoluteUrl,
  getCdnUrl,
  isSameOrigin,
  urlEncode,
  baseCdnUrl
}
